import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { CAD_DOCUMENTS_EXTENSIONS } from "@clovis/server/src/app/config/pdftron/extensions";
import create from "zustand";
import { persist } from "zustand/middleware";
import { captureException } from "~/config/sentry";
import type { FileViewer_DataQuery } from "~/screens/App/components/FileViewerDialog/components/FileViewer/FileViewer.graphql";
import { base64ToBlob, blobToBase64 } from "./blobUtils";
import {
  deleteFromIndexedDB,
  getFromIndexedDB,
  saveToIndexedDB,
} from "./indexedDBStorage";
import { returnPWD } from "./utils";

type File = FileViewer_DataQuery;

interface OfflineFile {
  id: string;
  name: string;
  storedAt: string;
  path: string;
  mimeType: string;
  size: number;
  file: File;
}

interface OfflineFilesState {
  files: OfflineFile[];
  isStoring: boolean;
  addFile: (file: OfflineFile) => Promise<void>;
  updateFile: (
    fileVersionId: string,
    location: { pageNumber: number; x: number; y: number },
    labels: unknown[],
    taskNumber: number
  ) => void;
  removeFile: (fileId: string) => Promise<void>;
  getStoredOfflineFile: (fileId: string) => OfflineFile | undefined;
  storeFileOffline: (file: File, tempUrl?: string) => Promise<void>;
  removeFiles: (fileIds: string[]) => Promise<void>;
  setStoring: (isStoring: boolean) => void;
  getStoredFile: (fileID: string) => Promise<Blob | undefined>;
  getFilesOfProject: (projectId: string) => OfflineFile[];
  getFilePWD: (fileId: string, folderNames: Map<string, string>) => string;
}

const isNative = Capacitor.isNativePlatform();

export const useOfflineFilesStore = create(
  persist<OfflineFilesState>(
    (set, get) => ({
      addFile: async (file) => {
        set((state) => ({
          files: state.files.filter((f) => f.id !== file.id).concat(file),
        }));
      },
      files: [],

      getFilePWD: (fileId, folderNames) => {
        const file = get().files.find((f) => f.id === fileId);
        if (!file) return "";
        return returnPWD(file, folderNames);
      },

      getFilesOfProject: (projectId) => {
        return get().files.filter(
          (f) => f.file?.files_by_pk?.project.id === projectId
        );
      },

      getStoredFile: async (fileID) => {
        const files: OfflineFile[] = get().files;
        const file = files.find((f) => f.id === fileID);
        if (!file) return;

        if (isNative) {
          try {
            const { data } = await Filesystem.readFile({
              directory: Directory.Data,
              path: file.path,
            });

            if (typeof data === "string") {
              return await base64ToBlob(data, file.mimeType);
            } else if (data instanceof Blob) {
              return data;
            }
          } catch (error) {
            console.error("Error reading native file:", error);
            captureException(error, {
              extra: {
                action: "getStoredFile",
                fileId: fileID,
              },
            });
            return undefined;
          }
        } else {
          try {
            const blob = await getFromIndexedDB(file.path);
            if (!blob) {
              console.error("No blob found in IndexedDB for path:", file.path);
              return undefined;
            }
            return blob;
          } catch (error) {
            console.error("Error retrieving file from IndexedDB:", error);
            captureException(error, {
              extra: {
                action: "getStoredFile",
                fileId: fileID,
              },
            });
            return undefined;
          }
        }
      },

      getStoredOfflineFile: (fileId) => {
        return get().files.find((f) => f.id === fileId);
      },

      isStoring: false,

      removeFile: async (fileId) => {
        const currentFiles = get().files;
        const fileToRemove = currentFiles.find((f) => f.id === fileId);

        if (fileToRemove) {
          try {
            const filePath = fileToRemove.path;

            if (isNative) {
              await Filesystem.deleteFile({
                directory: Directory.Data,
                path: filePath,
              });

              const pathParts = filePath.split("/");
              pathParts.pop();
              const dirPath = pathParts.join("/");

              if (dirPath) {
                await Filesystem.rmdir({
                  directory: Directory.Data,
                  path: dirPath,
                  recursive: true,
                });
              }
            } else {
              await deleteFromIndexedDB(filePath);
            }

            set((state) => ({
              files: state.files.filter((f) => f.id !== fileId),
            }));
          } catch (error) {
            captureException(error, {
              extra: {
                action: "removeFile",
                fileId,
              },
            });
          }
        }
      },

      removeFiles: async (fileIds) => {
        for (const fileId of fileIds) {
          await get().removeFile(fileId);
        }
      },

      setStoring: (isStoring) => set({ isStoring }),

      // tempUrl is a url containing the converted CAD file url into PDF
      storeFileOffline: async (fileViewer, tempUrl) => {
        const { files_by_pk: file } = fileViewer;
        if (!file) return;

        const { id, name, url } = file;
        if (!url) return;

        const fileExtension = name.split(".").pop() ?? "";
        const isCadFile = CAD_DOCUMENTS_EXTENSIONS.includes(fileExtension);

        try {
          let filePath = "";
          set({ isStoring: true });

          const response = await fetch(isCadFile && tempUrl ? tempUrl : url);
          const blob = await response.blob();
          const directory = `offline-files/${id}`;

          filePath = isCadFile
            ? `${directory}/${name}.pdf`
            : `${directory}/${name}`;

          if (isNative) {
            const base64Data = await blobToBase64(blob);

            try {
              await Filesystem.readdir({
                directory: Directory.Data,
                path: directory,
              });
            } catch (error) {
              await Filesystem.mkdir({
                directory: Directory.Data,
                path: directory,
                recursive: true,
              });
            }

            await Filesystem.writeFile({
              data: base64Data,
              directory: Directory.Data,
              path: filePath,
            });
          } else {
            await saveToIndexedDB(filePath, blob);
          }

          const offlineFile: OfflineFile = {
            file: fileViewer,
            id,
            mimeType: blob.type,
            name,
            path: filePath,
            size: blob.size,
            storedAt: new Date().toISOString(),
          };

          await get().addFile(offlineFile);
          return;
        } catch (error) {
          console.error("Error storing file offline:", error);
          captureException(error, {
            extra: {
              action: "storeFileOffline",
              fileId: id,
              fileName: name,
            },
          });
        } finally {
          set({ isStoring: false });
        }
      },

      updateFile: async (fileVersionId, location, labels, taskNumber) => {
        const file = get().files;
        if (!file) return;

        const updatedFiles = file.map((f) => {
          return {
            ...f,
            file: {
              ...f.file,
              files_by_pk: {
                ...f.file.files_by_pk,
                id: f.file.files_by_pk?.id ?? "",
                versions: f.file.files_by_pk?.versions?.map((v) => {
                  if (v.id === fileVersionId) {
                    return {
                      ...v,
                      tasks: [
                        ...v.tasks,
                        {
                          location: {
                            page_number: location.pageNumber,
                            pdf_location: true,
                            x: location.x,
                            y: location.y,
                          },
                          task: {
                            is_validated_by: null,
                            labels: labels,
                            number: taskNumber,
                          },
                        },
                      ],
                    };
                  }
                  return v;
                }),
              },
            },
          };
        });

        set({ files: updatedFiles as OfflineFile[] });
      },
    }),
    {
      getStorage: () => ({
        getItem: (name) => {
          return localStorage.getItem(name);
        },
        setItem: (name, value) => {
          localStorage.setItem(name, value);
        },
      }),
      name: "offline-files-storage",
      partialize: (state) => ({ files: state.files }),
    }
  )
);

export type { File, OfflineFile };
